var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "@application/store/useStore";
import { updateNode } from "@reducers/nodes/thunk/nodes-thunk";
import { toast } from "@ui/utils/toaster";
import { canEditFreeRoamingNode } from "@utility/permission-checker";
import { invalidateFreeRoamingSummary } from "@ui/hooks/useFreeRoamingNodesSummary";
import { secondsToHHmm, hhmmToSeconds, isValidHHmm, } from "../utils/time-format";
import { defaultNodeTypeThreeFormValues, isNewHardwareNodeType, toNodeTypeThreeConfig, toNodeTypeThreeFormValues, } from "../edit-node/nodeTypeThreeForm";
export const useNodeForm = ({ isEdit, node }) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { t } = useTranslation(["form", "validation", "common"]);
    const { updateLoading, updateError } = useAppSelector((state) => state.nodes);
    // Form setup with default values
    const methods = useForm({
        defaultValues: {
            id: "",
            nodeCategoryId: "",
            nodeId: "",
            nodeName: "",
            lastUpdated: "",
            nodeDetails: "",
            // Common
            doorAlarmDelay: "00:01",
            tempAlarmDelay: "00:01",
            installationDate: "",
            // NodeTypeTwo (WPT)
            measurementInterval: 1200,
            communicationInterval: 2400,
            temperatureOffset: 0.0,
            temperatureHighAlarm: 40,
            temperatureLowAlarm: -24,
            // NodeTypeOne (FRICOM)
            leftBadge: "",
            rightBadge: "",
            tempLow: -24,
            tempHigh: 40,
            overtimeHours: 0,
            bakeTime1: 0,
            bakeTime2: 0,
            bakeTime3: 0,
            bakeTime4: 0,
            bakeTime5: 0,
            bakeTime6: 0,
            onTime1: 0,
            offTime1: 0,
            onTime2: 0,
            offTime2: 0,
            onTime3: 0,
            offTime3: 0,
            displayPercent: 0,
            buzzerPercent: 0,
            commandFlags: 0,
            // New hardware (codes 3 and 100)
            nodeTypeThree: defaultNodeTypeThreeFormValues(),
            // Admin-only routing switch — off unless the node explicitly has it set
            freeRoamingNode: false,
        },
    });
    const { control, handleSubmit, reset, formState: { errors, isSubmitting }, } = methods;
    // Reset form when node data changes
    useEffect(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9, _10, _11;
        if (node) {
            const wpt = node.nodeTypeTwoConfig;
            const fricom = node.nodeTypeOneConfig;
            reset({
                id: (_a = node.id) !== null && _a !== void 0 ? _a : "",
                nodeCategoryId: (_d = (_c = (_b = node.nodeCategories) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.id) !== null && _d !== void 0 ? _d : "",
                nodeId: (_e = node.nodeId) !== null && _e !== void 0 ? _e : "",
                nodeName: (_f = node.nodeName) !== null && _f !== void 0 ? _f : "",
                lastUpdated: (_g = node.lastUpdated) !== null && _g !== void 0 ? _g : "",
                nodeDetails: (_h = node.nodeDetails) !== null && _h !== void 0 ? _h : "",
                // Common
                doorAlarmDelay: secondsToHHmm((_j = node.doorAlarmDelay) !== null && _j !== void 0 ? _j : 60),
                tempAlarmDelay: secondsToHHmm((_k = node.tempAlarmDelay) !== null && _k !== void 0 ? _k : 60),
                installationDate: (_l = node.installationDate) !== null && _l !== void 0 ? _l : "",
                // NodeTypeTwo (WPT)
                measurementInterval: (_m = wpt === null || wpt === void 0 ? void 0 : wpt.measurmentIntervals) !== null && _m !== void 0 ? _m : 1200,
                communicationInterval: (_o = wpt === null || wpt === void 0 ? void 0 : wpt.communicationIntervals) !== null && _o !== void 0 ? _o : 2400,
                temperatureOffset: (wpt === null || wpt === void 0 ? void 0 : wpt.tempratureOffSet)
                    ? parseFloat(wpt.tempratureOffSet)
                    : 0.0,
                temperatureHighAlarm: (_p = wpt === null || wpt === void 0 ? void 0 : wpt.tempratureHighAlaram) !== null && _p !== void 0 ? _p : 40,
                temperatureLowAlarm: (_q = wpt === null || wpt === void 0 ? void 0 : wpt.tempratureLowAlaram) !== null && _q !== void 0 ? _q : -24,
                // NodeTypeOne (FRICOM)
                leftBadge: (_r = fricom === null || fricom === void 0 ? void 0 : fricom.leftBadge) !== null && _r !== void 0 ? _r : "",
                rightBadge: (_s = fricom === null || fricom === void 0 ? void 0 : fricom.rightBadge) !== null && _s !== void 0 ? _s : "",
                tempLow: (_t = fricom === null || fricom === void 0 ? void 0 : fricom.tempLow) !== null && _t !== void 0 ? _t : -24,
                tempHigh: (_u = fricom === null || fricom === void 0 ? void 0 : fricom.tempHigh) !== null && _u !== void 0 ? _u : 40,
                overtimeHours: (_v = fricom === null || fricom === void 0 ? void 0 : fricom.overtimeHours) !== null && _v !== void 0 ? _v : 0,
                bakeTime1: (_w = fricom === null || fricom === void 0 ? void 0 : fricom.bakeTime1) !== null && _w !== void 0 ? _w : 0,
                bakeTime2: (_x = fricom === null || fricom === void 0 ? void 0 : fricom.bakeTime2) !== null && _x !== void 0 ? _x : 0,
                bakeTime3: (_y = fricom === null || fricom === void 0 ? void 0 : fricom.bakeTime3) !== null && _y !== void 0 ? _y : 0,
                bakeTime4: (_z = fricom === null || fricom === void 0 ? void 0 : fricom.bakeTime4) !== null && _z !== void 0 ? _z : 0,
                bakeTime5: (_0 = fricom === null || fricom === void 0 ? void 0 : fricom.bakeTime5) !== null && _0 !== void 0 ? _0 : 0,
                bakeTime6: (_1 = fricom === null || fricom === void 0 ? void 0 : fricom.bakeTime6) !== null && _1 !== void 0 ? _1 : 0,
                onTime1: (_2 = fricom === null || fricom === void 0 ? void 0 : fricom.onTime1) !== null && _2 !== void 0 ? _2 : 0,
                offTime1: (_3 = fricom === null || fricom === void 0 ? void 0 : fricom.offTime1) !== null && _3 !== void 0 ? _3 : 0,
                onTime2: (_4 = fricom === null || fricom === void 0 ? void 0 : fricom.onTime2) !== null && _4 !== void 0 ? _4 : 0,
                offTime2: (_5 = fricom === null || fricom === void 0 ? void 0 : fricom.offTime2) !== null && _5 !== void 0 ? _5 : 0,
                onTime3: (_6 = fricom === null || fricom === void 0 ? void 0 : fricom.onTime3) !== null && _6 !== void 0 ? _6 : 0,
                offTime3: (_7 = fricom === null || fricom === void 0 ? void 0 : fricom.offTime3) !== null && _7 !== void 0 ? _7 : 0,
                displayPercent: (_8 = fricom === null || fricom === void 0 ? void 0 : fricom.displayPercent) !== null && _8 !== void 0 ? _8 : 0,
                buzzerPercent: (_9 = fricom === null || fricom === void 0 ? void 0 : fricom.buzzerPercent) !== null && _9 !== void 0 ? _9 : 0,
                commandFlags: (_10 = fricom === null || fricom === void 0 ? void 0 : fricom.commandFlags) !== null && _10 !== void 0 ? _10 : 0,
                nodeTypeThree: toNodeTypeThreeFormValues(node.nodeTypeThreeConfig),
                freeRoamingNode: (_11 = node.freeRoamingNode) !== null && _11 !== void 0 ? _11 : false,
            });
        }
    }, [node, reset]);
    // Validation rules
    const validationRules = {
        nodeId: {
            required: t("validation:required", { field: "Node ID" }),
        },
        nodeName: {
            required: t("validation:required", { field: "Node Name" }),
        },
        doorAlarmDelay: {
            required: t("validation:required", { field: "Door Alarm Delay" }),
            validate: (value) => isValidHHmm(value) || "Invalid format. Use HH:mm (e.g. 00:30)",
        },
        tempAlarmDelay: {
            required: t("validation:required", { field: "Temperature Alarm Delay" }),
            validate: (value) => isValidHHmm(value) || "Invalid format. Use HH:mm (e.g. 00:30)",
        },
        measurementInterval: {
            required: t("validation:required", { field: "Measurement Interval" }),
            min: {
                value: 1,
                message: t("validation:min", { field: "Measurement Interval", min: 1 }),
            },
        },
        communicationInterval: {
            required: t("validation:required", { field: "Communication Interval" }),
            min: {
                value: 1,
                message: t("validation:min", {
                    field: "Communication Interval",
                    min: 1,
                }),
            },
        },
        installationDate: {
            required: t("validation:required", { field: "Installation Date" }),
        },
        temperatureOffset: {
            required: t("validation:required", { field: "Temperature Offset" }),
        },
        temperatureHighAlarm: {
            required: t("validation:required", { field: "Temperature High Alarm" }),
        },
        temperatureLowAlarm: {
            required: t("validation:required", { field: "Temperature Low Alarm" }),
        },
    };
    // Form submission handler for General Settings
    const handleFormSubmit = (data) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e;
        try {
            if (isEdit && (node === null || node === void 0 ? void 0 : node.id)) {
                const nodeTypeCode = (_a = node.nodeTypeCode) !== null && _a !== void 0 ? _a : 2;
                const command = {
                    id: node.id,
                    nodeUid: data.nodeId,
                    nodeName: data.nodeName,
                    nodeDetails: (_b = data.nodeDetails) !== null && _b !== void 0 ? _b : undefined,
                    accessPointUid: node.deviceId || undefined,
                    isActive: node.isActive,
                    nodeTypeCode,
                    // Common configuration (convert HH:mm back to seconds for API)
                    doorAlaramDelay: hhmmToSeconds(data.doorAlarmDelay),
                    tempAlaramDelay: hhmmToSeconds(data.tempAlarmDelay),
                    installationDate: data.installationDate,
                    nodeCategoryIds: data.nodeCategoryId ? [data.nodeCategoryId] : [],
                    isGeneralSettings: true,
                    isAlarmSettings: false,
                };
                // Super Admin only. Omitting the field for anyone else keeps ordinary user edits from
                // touching the flag; the server refuses an unauthorised change with 403 regardless.
                if (canEditFreeRoamingNode()) {
                    command.freeRoamingNode = (_c = data.freeRoamingNode) !== null && _c !== void 0 ? _c : false;
                }
                // Attach type-specific configuration
                if (nodeTypeCode === 2) {
                    command.nodeTypeTwoConfig = {
                        measurmentIntervals: Number(data.measurementInterval) || 120,
                        communicationIntervals: Number(data.communicationInterval) || 240,
                        tempratureOffSet: (_d = data.temperatureOffset) === null || _d === void 0 ? void 0 : _d.toString(),
                        tempratureHighAlaram: data.temperatureHighAlarm,
                        tempratureLowAlaram: data.temperatureLowAlarm,
                    };
                }
                else if (nodeTypeCode === 1) {
                    command.nodeTypeOneConfig = {
                        leftBadge: data.leftBadge,
                        rightBadge: data.rightBadge,
                        tempLow: data.tempLow,
                        tempHigh: data.tempHigh,
                        overtimeHours: data.overtimeHours,
                        bakeTime1: data.bakeTime1,
                        bakeTime2: data.bakeTime2,
                        bakeTime3: data.bakeTime3,
                        bakeTime4: data.bakeTime4,
                        bakeTime5: data.bakeTime5,
                        bakeTime6: data.bakeTime6,
                        onTime1: data.onTime1,
                        offTime1: data.offTime1,
                        onTime2: data.onTime2,
                        offTime2: data.offTime2,
                        onTime3: data.onTime3,
                        offTime3: data.offTime3,
                        displayPercent: data.displayPercent,
                        buzzerPercent: data.buzzerPercent,
                        commandFlags: data.commandFlags,
                    };
                }
                else if (isNewHardwareNodeType(nodeTypeCode)) {
                    command.nodeTypeThreeConfig = toNodeTypeThreeConfig((_e = data.nodeTypeThree) !== null && _e !== void 0 ? _e : defaultNodeTypeThreeFormValues(), toNodeTypeThreeFormValues(node.nodeTypeThreeConfig));
                }
                const result = yield dispatch(updateNode(command)).unwrap();
                // The Free Roaming Nodes sidebar item depends on this flag, so drop the cached answer.
                if (command.freeRoamingNode !== undefined) {
                    invalidateFreeRoamingSummary();
                }
                toast.success(t("common:toasts.updateSuccessfulWithTime", {
                    time: result.configurationTime,
                }));
            }
            else {
                toast.info(t("common:toasts.createNodeComingSoon"));
            }
        }
        catch (error) {
            toast.error((error === null || error === void 0 ? void 0 : error.message) || t("common:toasts.failedToSaveNode"));
        }
    });
    return {
        methods,
        control,
        errors,
        handleSubmit,
        validationRules,
        handleFormSubmit,
        isSubmitting: isSubmitting || updateLoading,
        updateError,
        dispatch,
        node,
    };
};
